import React from 'react';
interface DataPoint {
  x: number; // 0-100 (Exploration -> Execution)
  y: number; // 0-100 (Intensity/Depth)
}
interface SystemSeismographProps {
  data?: DataPoint[];
  className?: string;
  label?: string;
}
export function SystemSeismograph({
  data = [],
  className = '',
  label = 'System Pattern'
}: SystemSeismographProps) {
  // Default data if none provided (flat line with minor noise)
  const points = data.length > 0 ? data : [{
    x: 0,
    y: 20
  }, {
    x: 20,
    y: 30
  }, {
    x: 40,
    y: 25
  }, {
    x: 60,
    y: 35
  }, {
    x: 80,
    y: 20
  }, {
    x: 100,
    y: 30
  }];
  // Generate SVG path
  // We'll create a smooth curve through the points
  // Simple approach: Catmull-Rom splines or just cubic beziers between points
  // For a seismograph look, maybe just a polyline with smoothing?
  // Let's use a simple smoothing function
  const getPath = (points: DataPoint[]) => {
    if (points.length === 0) return '';
    // Scale points to viewbox (width 1000, height 200)
    // x: 0-100 -> 0-1000
    // y: 0-100 -> 200-0 (inverted, 0 is bottom)
    // Actually, let's make 0 bottom and 100 top.
    // So y=0 -> 200, y=100 -> 0.
    // Wait, "Deeper dips = where system lingers". Maybe "depth" means going DOWN?
    // "Vertical wave = intensity/depth (rises and falls)"
    // Let's assume typical graph: higher Y = higher intensity.
    const scaledPoints = points.map(p => ({
      x: p.x * 10,
      y: 200 - p.y * 2 // Scale y to 0-200 height
    }));
    // Start path
    let d = `M ${scaledPoints[0].x} ${scaledPoints[0].y}`;
    // Simple line to start
    for (let i = 1; i < scaledPoints.length; i++) {
      // Use cubic bezier for smoothing
      const p0 = scaledPoints[i - 1];
      const p1 = scaledPoints[i];
      // Control points
      const cp1x = p0.x + (p1.x - p0.x) / 2;
      const cp1y = p0.y;
      const cp2x = p0.x + (p1.x - p0.x) / 2;
      const cp2y = p1.y;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
    }
    return d;
  };
  const pathD = getPath(points);
  // Generate fill path (close the loop at the bottom)
  const fillD = `${pathD} L 1000 200 L 0 200 Z`;
  return <div className={`w-full bg-[#0A0A0A] rounded-2xl border border-[#2B2B2B] overflow-hidden ${className}`} data-id="element-279">
      <div className="p-6" data-id="element-280">
        <div className="flex justify-between items-center mb-4" data-id="element-281">
          <h3 className="text-sm font-medium text-[#F5F5F5]/80 uppercase tracking-wider" data-id="element-282">
            {label}
          </h3>
          <div className="flex gap-4 text-xs text-[#F5F5F5]/40 font-mono" data-id="element-283">
            <span data-id="element-284">EXPLORATION</span>
            <span data-id="element-285">EXECUTION</span>
          </div>
        </div>

        <div className="relative w-full h-[200px] md:h-[250px]" data-id="element-286">
          {/* Background Grid Lines */}
          <div className="absolute inset-0 flex justify-between pointer-events-none" data-id="element-287">
            {[0, 25, 50, 75, 100].map(pos => <div key={pos} className="h-full w-px bg-[#2B2B2B]/50" style={{
            left: `${pos}%`,
            position: 'absolute'
          }} data-id="element-288" />)}
          </div>

          {/* Horizontal Axis Line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-[#2B2B2B]" data-id="element-289" />

          <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="w-full h-full overflow-visible" data-id="element-290">
            <defs data-id="element-291">
              <linearGradient id="waveGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-292">
                <stop offset="0%" stopColor="#B89B62" stopOpacity="0.2" data-id="element-293" />
                <stop offset="100%" stopColor="#B89B62" stopOpacity="0" data-id="element-294" />
              </linearGradient>
            </defs>

            {/* The Fill */}
            <path d={fillD} fill="url(#waveGradient)" className="transition-all duration-1000 ease-in-out" data-id="element-295" />

            {/* The Wave Line */}
            <path d={pathD} fill="none" stroke="#B89B62" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-1000 ease-in-out drop-shadow-[0_4px_6px_rgba(184,155,98,0.2)]" data-id="element-296" />

            {/* Repeat Markers (Vertical Lines at peaks) */}
            {points.map((p, i) => p.y > 50 && <line key={i} x1={p.x * 10} y1={200 - p.y * 2} x2={p.x * 10} y2={200} stroke="#2B2B2B" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" data-id="element-297" />)}
          </svg>
        </div>

        <div className="mt-4 flex justify-between text-xs text-[#F5F5F5]/30 font-mono" data-id="element-298">
          <span data-id="element-299">0%</span>
          <span data-id="element-300">50%</span>
          <span data-id="element-301">100%</span>
        </div>
      </div>
    </div>;
}